import { Info, Paperclip, SlidersHorizontal } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { submitLogInsightsReview } from "../../../api/logInsightsApi";
import {
  fetchFilterOptions,
  type LogReviewChartFilters,
  type LogReviewFilterOptions,
} from "../../../api/logReviewApi";
import { fetchInfoDetails, fetchServerNodes } from "../../../api/logReviewPopupApi";
import { useLogInsightsCharts } from "../../../hooks/useLogInsightsCharts";
import { useLogInsightsList } from "../../../hooks/useLogInsightsList";
import { useSirarFilterTree } from "../../../hooks/useSirarFilterTree";
import { useAuthStore } from "../../../stores/authStore";
import { useToastStore } from "../../../stores/toastStore";
import { DEFAULT_TABLE_PAGE_SIZE } from "../../../utils/formatters";
import {
  ChartCard,
  chartTitleWithBadge,
  DonutPieChart,
  TreeMap,
} from "../../components/ChartsComponent";
import { DataTable, type Column } from "../../components/DataTable";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { StatusBadge } from "../../components/StatusBadge";
import { TableToolbar } from "../../components/TableToolbar";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../components/ui/utils";
import {
  LOG_INSIGHT_CONFIG,
  type LogInsightRole,
  type LogInsightRow,
  type LogReviewDrawerFilterKey,
  type LogReviewJustificationHistory,
  type LogReviewMultiFilters,
  type LogReviewServerNode,
} from "../../types/logReview";
import {
  LOG_REVIEW_DONUT_PROPS,
  riskVariant,
  TEAM_HEATMAP_COLORS,
  withDonutColors,
  withRequestStatusColors,
} from "../LogReview/logReviewChartUtils";
import {
  LogReviewFilterDrawer,
  type DrawerFilterFieldConfig,
} from "../LogReview/LogReviewFilterDrawer";
import { LogReviewInfoModal } from "../LogReview/LogReviewInfoModal";
import { LogReviewServersModal } from "../LogReview/LogReviewServersModal";
import {
  LogReviewTopFilterBar,
  type LogReviewTopFilters,
} from "../LogReview/LogReviewTopFilterBar";
import { AttachmentsModal } from "./AttachmentsModal";
import { LogInsightsDecisionModal } from "./LogInsightsDecisionModal";

const DEFAULT_SORT_COLUMN = "updatedDt";

/** UI column id → backend sort column for the Details list payload. */
const INSIGHTS_SORT_COLUMNS: Record<string, string> = {
  changeRequestId: "changeRequestId",
  application: "application",
  division: "division",
  risk: "risk",
  team: "team",
  users: "users",
  plannedStart: "plannedStartTime",
  plannedEnd: "plannedEndTime",
  actualStart: "actualStartTime",
  actualEnd: "actualEndTime",
  servers: "servers",
  status: "status",
  reasons: "reasonForNonCompliance",
  justifiedBy: "requestedBy",
  justifiedOn: "requestTime",
  attachmentCount: "attachmentCount",
};

/** Columns hidden by default — toggleable via "More Fields". */
const HIDDEN_BY_DEFAULT: Record<string, boolean> = {
  division: false,
  justifiedBy: false,
  justifiedOn: false,
  plannedStart: false,
  plannedEnd: false,
  actualStart: false,
  actualEnd: false,
  users: false,
  servers: false,
  reasons: false,
};

/** Columns that are always on — not offered under "More Fields". */
const PINNED_COLUMNS = [
  "changeRequestId",
  "application",
  "risk",
  "team",
  "status",
  "attachmentCount",
  "info",
];

const COLUMN_LABELS: Record<string, string> = {
  changeRequestId: "CR Number",
  application: "Application Name",
  division: "Division",
  risk: "Risk",
  team: "Team",
  users: "User ID",
  plannedStart: "Planned Start",
  plannedEnd: "Planned End",
  actualStart: "Actual Start",
  actualEnd: "Actual End",
  servers: "Servers/Nodes",
  status: "Status",
  reasons: "Reason(s) for Non-Compliance",
  justifiedBy: "Justified By",
  justifiedOn: "Justified On",
  attachmentCount: "Attachment(s)",
  info: "Info",
};

const pad = (n: number) => `${n}`.padStart(2, "0");
const isoDate = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Default payload window — first day of the current month through today.
const defaultFrom = () => {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};
const defaultTo = () => isoDate(new Date());
const uniq = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

export interface LogInsightsViewProps {
  role: LogInsightRole;
}

/**
 * Shared view behind both insights screens.
 *
 * Reviewer (Team Lead) and Approver (Division Head) run the same layout —
 * three charts over a server-driven Details table with a bulk decision
 * action. Only the endpoints, status vocabulary and button copy differ, and
 * those come from LOG_INSIGHT_CONFIG[role].
 */
export const LogInsightsView = ({ role }: LogInsightsViewProps) => {
  const cfg = LOG_INSIGHT_CONFIG[role];
  const showError = useToastStore((s) => s.showError);
  const showSuccess = useToastStore((s) => s.showSuccess);
  const commentBy = useAuthStore((s) => s.user?.username ?? "");

  // Cascading Division → Application → Category tree for the top filter bar
  // (shared with the main Log Review dashboard).
  const { tree, loading: treeLoading } = useSirarFilterTree();
  const allAppCodes = useMemo(
    () => uniq(tree.flatMap((d) => d.applications.map((a) => a.appCode))),
    [tree],
  );
  const allCategories = useMemo(
    () => uniq(tree.flatMap((d) => d.applications.flatMap((a) => a.categories))),
    [tree],
  );
  const appNameToCode = useMemo(() => {
    const map = new Map<string, string>();
    tree.forEach((d) => d.applications.forEach((a) => map.set(a.appName, a.appCode)));
    return map;
  }, [tree]);

  const toChartFilters = useCallback(
    (top: Partial<LogReviewTopFilters>): LogReviewChartFilters => {
      const apps = top.application ?? [];
      const cats = top.category ?? [];
      const from = top.dateRange?.from ?? defaultFrom();
      const to = top.dateRange?.to ?? defaultTo();
      return {
        appCode: apps.length
          ? apps.map((n) => appNameToCode.get(n)).filter((c): c is string => Boolean(c))
          : allAppCodes,
        category: cats.length ? cats : allCategories,
        startDate: `${from}T00:00:00.000`,
        endDate: `${to}T23:59:59.999`,
      };
    },
    [allAppCodes, allCategories, appNameToCode],
  );

  const [appliedTop, setAppliedTop] = useState<LogReviewTopFilters | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const chartFilters = useMemo<LogReviewChartFilters>(
    () => toChartFilters(appliedTop ?? {}),
    [toChartFilters, appliedTop],
  );

  // ── Table state (server-driven) ────────────────────────────────────────
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  // The typed box, and the keyword actually sent to the backend. Split so a
  // keystroke never reaches the API — the second only moves on submit.
  const [search, setSearch] = useState("");
  const [keyword, setKeyword] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [columnVisibility, setColumnVisibility] =
    useState<Record<string, boolean>>(HIDDEN_BY_DEFAULT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<LogReviewMultiFilters>({});
  const [infoRow, setInfoRow] = useState<LogInsightRow | null>(null);
  const [serverRow, setServerRow] = useState<LogInsightRow | null>(null);
  const [attachRow, setAttachRow] = useState<LogInsightRow | null>(null);
  const [decision, setDecision] = useState<"positive" | "negative" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Lazily-fetched popup payloads — reuse the main dashboard's endpoints.
  const [serverNodes, setServerNodes] = useState<LogReviewServerNode[]>([]);
  const [serverNodesLoading, setServerNodesLoading] = useState(false);
  const [infoHistory, setInfoHistory] = useState<LogReviewJustificationHistory[]>([]);
  const [infoLoading, setInfoLoading] = useState(false);

  // Hold the first fetch until the filter cascade has applied — collapses the
  // mount "called → cancelled → called" pattern into a single call.
  const ready = appliedTop !== null || (!treeLoading && tree.length === 0);

  const charts = useLogInsightsCharts(role, chartFilters, reloadKey, ready);
  const handleChartRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  // No active column sort ⇒ send empty sort fields; the backend then applies
  // its own default ordering.
  const mappedSort = sortKey ? INSIGHTS_SORT_COLUMNS[sortKey] : undefined;
  const sortByColumn = sortKey ? (mappedSort ?? DEFAULT_SORT_COLUMN) : "";
  const sortOrder: "asc" | "desc" | "" = sortKey ? sortDir : "";

  const list = useLogInsightsList({
    role,
    pageNumber: pageIndex,
    itemsPerPage: pageSize,
    keyword,
    sortByColumn,
    sortOrder,
    appCode: chartFilters.appCode,
    category: chartFilters.category,
    // Division is intentionally sent as [] for now.
    division: [],
    teams: filters.team ?? [],
    risk: filters.risk ?? [],
    status: filters.status ?? [],
    criticality: filters.criticality ?? [],
    startDate: chartFilters.startDate,
    endDate: chartFilters.endDate,
    reloadKey,
    enabled: ready,
  });

  // Applying / clearing the top filter resets the table's own state so results
  // start clean.
  const resetTableState = useCallback(() => {
    setPageIndex(0);
    // Both halves: clearing only the box would leave the committed keyword
    // still filtering the results it no longer appears to describe.
    setSearch("");
    setKeyword("");
    setSortKey(null);
    setFilters({});
    setSelection({});
    setColumnVisibility(HIDDEN_BY_DEFAULT);
  }, []);

  const handleApply = useCallback(
    (top: LogReviewTopFilters) => {
      setAppliedTop(top);
      resetTableState();
      setReloadKey((k) => k + 1);
    },
    [resetTableState],
  );

  const handleClear = useCallback(() => {
    setAppliedTop(null);
    resetTableState();
    setReloadKey((k) => k + 1);
  }, [resetTableState]);

  /**
   * Commit the typed box to the backend keyword. Typing alone never hits the
   * API; this runs on Enter, on the search button, and when the box is
   * cleared. Bails when nothing changed so a stray Enter is not a refetch.
   */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPageIndex(0);
  };

  // asc → desc → unsorted. The third click clears the sort so the backend
  // reapplies its own default ordering.
  const handleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
    setPageIndex(0);
  };

  // Drawer filter options (risk / teams) — server-sourced and shared with the
  // main dashboard. Status is fixed to the role's own workflow vocabulary.
  const [filterOptions, setFilterOptions] = useState<LogReviewFilterOptions>({
    risk: [],
    teams: [],
    status: [],
  });

  useEffect(() => {
    const controller = new AbortController();
    fetchFilterOptions(controller.signal)
      .then(setFilterOptions)
      .catch(() => {
        /* leave options empty on failure — the drawer still opens */
      });
    return () => controller.abort();
  }, []);

  // Servers/Nodes popup — fetch by finding id when opened.
  useEffect(() => {
    if (!serverRow) return;
    const controller = new AbortController();
    setServerNodesLoading(true);
    setServerNodes([]);
    fetchServerNodes(serverRow.id, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setServerNodes(rows);
        setServerNodesLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError(
          cfg.title,
          err instanceof Error ? err.message : "Failed to load servers",
        );
        setServerNodesLoading(false);
      });
    return () => controller.abort();
  }, [serverRow, showError, cfg.title]);

  // Info popup — justification history by finding id (same `getInfoPopUp/{id}`
  // endpoint as the main Log Review dashboard).
  useEffect(() => {
    if (!infoRow) return;
    const controller = new AbortController();
    setInfoLoading(true);
    setInfoHistory([]);
    fetchInfoDetails(infoRow.id, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setInfoHistory(rows);
        setInfoLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError(cfg.title, err instanceof Error ? err.message : "Failed to load info");
        setInfoLoading(false);
      });
    return () => controller.abort();
  }, [infoRow, showError, cfg.title]);

  const filterFields: DrawerFilterFieldConfig[] = useMemo(
    () => [
      { key: "risk", label: "Risk", options: filterOptions.risk },
      { key: "team", label: "Team", options: filterOptions.teams },
      {
        key: "status",
        // Fixed to the role's own workflow statuses: reviewer → Pending
        // Review / Rejected / Acknowledged; approver → Pending Approval /
        // Rejected / Approved.
        label: "Status",
        options: [cfg.pendingStatus, cfg.negativeStatus, cfg.positiveStatus],
      },
    ],
    [filterOptions, cfg],
  );

  // Selection is dropped when the RESULT SET changes, so this tracks the
  // committed keyword rather than the box. Keying it to the draft would wipe
  // a set of ticked rows on the first keystroke, before anything refetched.
  useEffect(() => {
    setSelection({});
  }, [filters, keyword]);

  // Only rows still awaiting this role's decision can be actioned, and only
  // when the list endpoint says this user may approve.
  //
  // TEMP: the `list.canApprove` half is commented out so the review workflow
  // can be exercised without the backend granting approval rights. Restore by
  // putting `list.canApprove &&` back — see the two other TEMP notes below.
  const isRowSelectable = (row: LogInsightRow) =>
    /* list.canApprove && */ row.status === cfg.pendingStatus;

  const selectableRows = useMemo(
    () => list.rows.filter(isRowSelectable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.rows, list.canApprove, cfg.pendingStatus],
  );

  const selectedIds = useMemo(
    () =>
      Object.entries(selection)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [selection],
  );

  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selection[r.id]);

  const toggleAll = (checked: boolean) => {
    setSelection(() => {
      if (!checked) return {};
      const next: Record<string, boolean> = {};
      selectableRows.forEach((r) => {
        next[r.id] = true;
      });
      return next;
    });
  };

  const allColumns = useMemo<Column<LogInsightRow>[]>(
    () => [
      {
        key: "changeRequestId",
        cellClassName: "!whitespace-nowrap",
        header: "CR Number",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "application",
        cellClassName: "!whitespace-nowrap",
        align: "left",
        header: "Application Name",
        sortable: true,
        headerAlign: "center",
      },
      { key: "division", cellClassName: "!whitespace-nowrap", align: "left", header: "Division", sortable: true, headerAlign: "center" },
      {
        key: "risk",
        header: "Risk",
        sortable: true,
        align: "center",
        headerAlign: "center",
        render: (row) => (
          <StatusBadge status={riskVariant(row.risk)} label={row.risk} dot={false} />
        ),
      },
      {
        key: "team",
        header: "Team",
        sortable: true,
        align: "left",
        headerAlign: "center",
        render: (row) => (
          <div className="flex flex-col">
            {row.team.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
        ),
      },
      {
        key: "users",
        align: "left",
        header: "User ID",
        sortable: true,
        headerAlign: "center",
        render: (row) => row.users.join(", "),
      },
      {
        key: "plannedStart",
        cellClassName: "!whitespace-nowrap",
        header: "Planned Start",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "plannedEnd",
        cellClassName: "!whitespace-nowrap",
        header: "Planned End",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "actualStart",
        cellClassName: "!whitespace-nowrap",
        header: "Actual Start",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "actualEnd",
        cellClassName: "!whitespace-nowrap",
        header: "Actual End",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "servers",
        cellClassName: "!whitespace-nowrap",
        header: "Servers/Nodes",
        sortable: true,
        align: "center",
        headerAlign: "center",
        render: (row) => (
          <button
            type="button"
            className="font-medium text-primary underline underline-offset-2"
            onClick={() => setServerRow(row)}
          >
            {row.serverNodes.length}
          </button>
        ),
      },
      {
        key: "status",
        cellClassName: "!whitespace-nowrap",
        header: "Status",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "reasons",
        align: "left",
        header: "Reason(s) for Non-Compliance",
        sortable: true,
        headerAlign: "center",
        maxChars: false,
        render: (row) => {
          if (!row.reasons.length) return "—";
          // List each reason on its own line, comma-separated.
          return (
            <div className="flex flex-col gap-0.5">
              {row.reasons.map((reason, i) => (
                <span key={`${reason}-${i}`}>
                  {reason}
                  {i < row.reasons.length - 1 ? "," : ""}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: "justifiedBy",
        align: "left",
        header: "Justified By",
        sortable: true,
        headerAlign: "center",
      },
      {
        key: "justifiedOn",
        cellClassName: "!whitespace-nowrap",
        header: "Justified On",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "attachmentCount",
        cellClassName: "!whitespace-nowrap",
        header: "Attachment(s)",
        sortable: true,
        align: "center",
        headerAlign: "center",
        render: (row) =>
          row.attachmentCount > 0 ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              onClick={() => setAttachRow(row)}
            >
              {row.attachmentCount}
              <Paperclip className="size-3.5" />
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "info",
        header: "Info",
        align: "center",
        headerAlign: "center",
        render: (row) => (
          <button
            type="button"
            aria-label={`Action plan details for ${row.changeRequestId}`}
            data-testid={`lr-insight-info-${row.id}`}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setInfoRow(row)}
          >
            <Info className="size-4" aria-hidden />
          </button>
        ),
      },
    ],
    [],
  );

  const visibleColumns = useMemo(() => {
    const cols = allColumns.filter((c) => columnVisibility[c.key] !== false);
    // TEMP: gate on the endpoint's approval right disabled, so the row-select
    // column always renders. Restore with: if (!list.canApprove) return cols;
    // if (!list.canApprove) return cols;

    const selectColumn: Column<LogInsightRow> = {
      // No explicit `width`: a single sized column puts the whole table into
      // table-fixed, which then splits every remaining column evenly and
      // squeezes the wide ones until their text breaks mid-word. `minWidth`
      // floors this column without triggering that.
      key: "__select",
      minWidth: 56,
      align: "center",
      headerAlign: "center",
      header: (
        <Checkbox
          checked={allSelected}
          onCheckedChange={(v) => toggleAll(v === true)}
          aria-label="Select all actionable rows"
          disabled={selectableRows.length === 0}
        />
      ),
      render: (row) => (
        <Checkbox
          checked={selection[row.id] === true}
          disabled={!isRowSelectable(row)}
          aria-label={`Select ${row.changeRequestId}`}
          onCheckedChange={(v) =>
            setSelection((prev) => ({ ...prev, [row.id]: v === true }))
          }
        />
      ),
    };
    return [selectColumn, ...cols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns, columnVisibility, list.canApprove, selection, allSelected, selectableRows]);

  const toggleableColumns = useMemo(
    () => allColumns.map((c) => c.key).filter((key) => !PINNED_COLUMNS.includes(key)),
    [allColumns],
  );

  // Sorted by LABEL, not by column order: this is a lookup list, and the
  // reader is scanning for a field name they already have in mind.
  const columnOptions = useMemo(
    () =>
      toggleableColumns
        .map((key) => ({ value: key, label: COLUMN_LABELS[key] ?? key }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [toggleableColumns],
  );

  // Note the inverted default here: this table hides only what is listed in
  // HIDDEN_BY_DEFAULT, so anything not explicitly `false` counts as visible.
  const visibleToggleableColumns = useMemo(
    () => toggleableColumns.filter((key) => columnVisibility[key] !== false),
    [toggleableColumns, columnVisibility],
  );

  // The dropdown hands back the full checked set, so every toggleable column
  // is restated rather than patched -- absent ones are explicitly `false`.
  const handleColumnSelectionChange = (next: string[]) => {
    setColumnVisibility((prev) => {
      const merged = { ...prev };
      toggleableColumns.forEach((key) => {
        merged[key] = next.includes(key);
      });
      return merged;
    });
  };

  const filterCount = useMemo(
    () =>
      (Object.keys(filters) as LogReviewDrawerFilterKey[]).reduce(
        (n, k) => n + ((filters[k]?.length ?? 0) > 0 ? 1 : 0),
        0,
      ),
    [filters],
  );

  const handleConfirm = async (comment: string) => {
    if (!decision) return;
    const ids = selectedIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) {
      setDecision(null);
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitLogInsightsReview(role, ids, decision, comment, commentBy);
      if (res.success) {
        showSuccess(cfg.title, res.message || "Status updated successfully");
        setDecision(null);
        setSelection({});
        // Refetch so the actioned rows reflect their new status.
        setReloadKey((k) => k + 1);
      } else {
        showError(cfg.title, res.message || "Failed to update status");
      }
    } catch (err) {
      showError(
        cfg.title,
        err instanceof Error ? err.message : "Failed to update status",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid={`log-insights-${role}`}>
      <LogReviewTopFilterBar
        divisions={tree}
        onApply={handleApply}
        onClear={handleClear}
      />

      <div className="grid grid-cols-12 gap-3">
        <ChartCard
          title="Request Status"
          className="col-span-12 min-w-0 lg:col-span-4"
        >
          <DonutPieChart
            {...LOG_REVIEW_DONUT_PROPS}
            name="log-insights-request-status"
            data={withRequestStatusColors(charts.requestStatus)}
            loading={charts.loading.requestStatus}
            error={charts.error.requestStatus}
            onRetry={handleChartRetry}
            emptyMessage="No data available"
            ariaLabel="Request status"
          />
        </ChartCard>

        <ChartCard
          title={chartTitleWithBadge("Non-Compliant Findings By Team", "Top-5")}
          className="col-span-12 min-w-0 lg:col-span-4"
        >
          <TreeMap
            data={charts.teamHeatmap}
            palette={TEAM_HEATMAP_COLORS}
            valueLabel="Findings"
            height={280}
            loading={charts.loading.teamHeatmap}
            error={charts.error.teamHeatmap}
            onRetry={handleChartRetry}
            ariaLabel="Non-Compliant findings by team"
          />
        </ChartCard>

        <ChartCard
          title="Compliance Status Overview"
          className="col-span-12 min-w-0 lg:col-span-4"
        >
          <DonutPieChart
            {...LOG_REVIEW_DONUT_PROPS}
            name="log-insights-compliance-status"
            data={withDonutColors(charts.complianceStatus)}
            loading={charts.loading.complianceStatus}
            error={charts.error.complianceStatus}
            onRetry={handleChartRetry}
            emptyMessage="No data available"
            ariaLabel="Compliance status overview"
          />
        </ChartCard>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-foreground">Details</h3>

        {/* The toolbar carries `flex-1` for the case where it shares a row
            with a sibling cluster. This parent is a flex COLUMN, so that grows
            it vertically instead and its wrapped lines drift apart across the
            free height. Clamping the height to its content stops the growth
            and hands the space to the table below. */}
        <TableToolbar
          className="max-h-fit"
          search={{
            value: search,
            // Typing only updates the box. `onSubmit` is what reaches the API,
            // matching every other table in the app — the toolbar fires it on
            // Enter, on the search button, and the moment the box is cleared.
            onChange: setSearch,
            onSubmit: submitSearch,
            placeholder: "Search",
            ariaLabel: "Search change requests",
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageIndex(0);
          }}
          actions={
            <>
              {/* `bg-card` overrides the outline variant's `bg-background`,
                  which would otherwise paint the button the colour of the
                  toolbar behind it. See the note in LogReviewTable. */}
              <Button
                variant="outline"
                size="sm"
                className="bg-card"
                onClick={() => setFiltersOpen(true)}
              >
                <SlidersHorizontal className="size-4" aria-hidden />
                Filters
                {filterCount > 0 ? (
                  <span className="ml-1 rounded-full bg-primary px-1.5 py-0.5 text-xs tabular-nums text-primary-foreground">
                    {filterCount}
                  </span>
                ) : null}
              </Button>

              {/* The column picker reuses the filter dropdown rather than a
                  bare menu, so it gets the search box and Select all / Clear
                  all that a list this long needs. */}
              <MultiSelectDropdown
                trigger={
                  <Button variant="outline" size="sm" className="bg-card">
                    <SlidersHorizontal className="size-4" aria-hidden />
                    More Fields
                  </Button>
                }
                selectAll
                options={columnOptions}
                selected={visibleToggleableColumns}
                onChange={handleColumnSelectionChange}
                searchable
              />

              {/* TEMP: the approval-right gate is off, so the decision buttons
                  always render. Restore by wrapping the two Buttons below in
                  `{list.canApprove ? ( <>...</> ) : null}` again. */}
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedIds.length === 0}
                onClick={() => setDecision("negative")}
              >
                {selectedIds.length > 0
                  ? `${cfg.negativeLabel} (${selectedIds.length})`
                  : cfg.negativeLabel}
              </Button>
              <Button
                size="sm"
                className={cn("bg-emerald-600 text-white hover:bg-emerald-600/90")}
                disabled={selectedIds.length === 0}
                onClick={() => setDecision("positive")}
              >
                {selectedIds.length > 0
                  ? `${cfg.positiveLabel} (${selectedIds.length})`
                  : cfg.positiveLabel}
              </Button>
            </>
          }
        />

        <DataTable<LogInsightRow>
          columns={visibleColumns}
          data={list.rows}
          rowKey={(row) => row.id}
          loading={list.loading}
          error={list.error}
          onRetry={handleChartRetry}
          emptyMessage="No Data Available"
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          // Zero-based, matching every other DataTable in the app. See the
          // note in LogReviewTable.
          page={pageIndex}
          pageSize={pageSize}
          totalItems={list.totalCount}
          onPageChange={setPageIndex}
          pagerStyle="simple"
          itemLabel="requests"
        />
      </div>

      <LogReviewFilterDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        fields={filterFields}
        applied={filters}
        onApply={(next) => {
          setFilters(next);
          setPageIndex(0);
        }}
      />

      <LogReviewInfoModal
        open={infoRow !== null}
        row={
          infoRow
            ? {
              changeRequestId: infoRow.changeRequestId,
              justificationHistory: infoHistory,
            }
            : null
        }
        isLoading={infoLoading}
        onOpenChange={(open) => {
          if (!open) setInfoRow(null);
        }}
      />

      <LogReviewServersModal
        open={serverRow !== null}
        row={serverRow ? { ...serverRow, serverNodes } : null}
        isLoading={serverNodesLoading}
        onOpenChange={(open) => {
          if (!open) setServerRow(null);
        }}
      />

      <AttachmentsModal
        open={attachRow !== null}
        row={attachRow}
        onOpenChange={(open) => {
          if (!open) setAttachRow(null);
        }}
      />

      <LogInsightsDecisionModal
        open={decision !== null}
        title={decision === "negative" ? cfg.negativeModalTitle : cfg.positiveModalTitle}
        confirmLabel={decision === "negative" ? cfg.negativeLabel : cfg.positiveLabel}
        confirmVariant={decision === "negative" ? "danger" : "success"}
        selectedCount={selectedIds.length}
        submitting={submitting}
        onOpenChange={(open) => {
          if (!open) setDecision(null);
        }}
        onConfirm={handleConfirm}
      />
    </div>
  );
};
